/**
 * Categorized hotel amenities catalog.
 * Used by:
 *  - Hotel Panel "Tesis Özellikleri" page (selection UI)
 *  - Public Hotel Detail page (grouped display)
 *
 * Keys are stable identifiers; values are the localized labels actually
 * stored in the DB and shown to guests.
 */
export const AMENITY_CATEGORIES = [
    {
        key: "general",
        label: "Tesis & Genel",
        icon: "Building2",
        color: "sky",
        items: [
            "Ücretsiz WiFi", "Ücretsiz Otopark", "Ücretli Otopark", "Vale Parking",
            "Resepsiyon 24 Saat", "Asansör", "Lobi", "Konsiyerj",
            "Çamaşırhane", "Kuru Temizleme", "Ütüleme Servisi", "Bagaj Saklama",
            "Aile Odası", "Sigara İçilmeyen Odalar", "Bebek Karyolası",
            "Tekerlekli Sandalye Erişimi", "Engelli Dostu", "ATM", "Döviz Bürosu",
            "Hediyelik Eşya Dükkanı", "Berber/Kuaför", "Market",
        ],
    },
    {
        key: "room",
        label: "Oda Özellikleri",
        icon: "BedDouble",
        color: "indigo",
        items: [
            "Klima", "Isıtma", "Mini Bar", "Mini Buzdolabı", "Kasa",
            "Saç Kurutma Makinesi", "Çay/Kahve Makinesi", "LED TV",
            "Smart TV", "Uydu Kanalları", "Kablo TV", "Netflix",
            "Telefon", "Banyo Küvet", "Duş Kabini", "Bornoz", "Terlik",
            "Yatak Takımı (Pamuk)", "King Size Yatak", "Queen Size Yatak",
            "Balkon", "Teras", "Deniz Manzarası", "Şehir Manzarası",
            "Bahçe Manzarası", "Dağ Manzarası", "Havuz Manzarası",
            "Şömine", "Yer Isıtması", "Ses Yalıtımı", "Karartma Perdesi",
        ],
    },
    {
        key: "food",
        label: "Yiyecek & İçecek",
        icon: "UtensilsCrossed",
        color: "amber",
        items: [
            "Restoran", "A la Carte Restoran", "Bar", "Pool Bar", "Snack Bar",
            "Lobby Bar", "Lounge", "Kafe", "Açık Büfe Kahvaltı",
            "Kahvaltı Dahil", "Akşam Yemeği Dahil", "Yarım Pansiyon",
            "Tam Pansiyon", "Her Şey Dahil", "Ultra Her Şey Dahil",
            "Oda Servisi", "Oda Servisi 24/7", "Özel Diyet (Vegan)",
            "Özel Diyet (Glutensiz)", "Çocuk Menüsü", "Şarap Mahzeni",
            "Barbekü", "Piknik Alanı",
        ],
    },
    {
        key: "pool",
        label: "Havuz & Plaj",
        icon: "Waves",
        color: "cyan",
        items: [
            "Açık Havuz", "Kapalı Havuz", "Isıtmalı Havuz", "Çocuk Havuzu",
            "Yarı Olimpik Havuz", "Sonsuzluk Havuzu", "Şezlong",
            "Şemsiye", "Havlu Servisi", "Su Kaydırağı", "Aquapark",
            "Özel Plaj", "Plaj Bar", "Su Sporları", "Jet Ski",
            "Banana Boat", "Iskele", "Marina", "Dalış Merkezi",
            "Sörf Alanı", "Plaj Voleybolu",
        ],
    },
    {
        key: "spa",
        label: "Spa & Wellness",
        icon: "Sparkles",
        color: "rose",
        items: [
            "Türk Hamamı", "Sauna", "Buhar Odası", "Jakuzi", "Şok Havuzu",
            "Tuz Odası", "Masaj", "Aroma Terapi", "Yüz Bakımı",
            "Vücut Bakımı", "Manikür/Pedikür", "Fitness Center",
            "Yoga Salonu", "Pilates", "Aerobik", "Kişisel Antrenör",
            "Wellness Programları",
        ],
    },
    {
        key: "activity",
        label: "Aktivite & Eğlence",
        icon: "Music",
        color: "violet",
        items: [
            "Tenis Kortu", "Tenis Dersi", "Basketbol Sahası",
            "Mini Futbol", "Plaj Voleybolu", "Masa Tenisi", "Bilardo",
            "Dart", "Bowling", "Atari Salonu", "Disko", "Gece Kulübü",
            "Canlı Müzik", "Animasyon Ekibi", "Dans Dersleri",
            "Yoga Dersleri", "Bisiklet Kiralama", "Doğa Yürüyüşü",
            "Mini Golf", "Go-Kart", "Atış Poligonu",
        ],
    },
    {
        key: "kids",
        label: "Çocuklar",
        icon: "Baby",
        color: "pink",
        items: [
            "Mini Club (4-12 yaş)", "Maxi Club (12+ yaş)",
            "Çocuk Oyun Alanı", "Çocuk Havuzu", "Çocuk Su Kaydırağı",
            "Çocuk Animasyonu", "Bebek Bakımı", "Kreş",
            "Mama Sandalyesi", "Bebek Karyolası", "Çocuk Spa",
            "Game Room", "Çocuk Menüsü", "Bebek Arabası Kiralama",
            "Çocuk Tuvaleti",
        ],
    },
    {
        key: "business",
        label: "Toplantı & Etkinlik",
        icon: "Briefcase",
        color: "slate",
        items: [
            "İş Merkezi", "Toplantı Salonu", "Konferans Salonu",
            "Düğün Salonu", "Banket Hizmeti", "Projeksiyon Cihazı",
            "Ses Sistemi", "Mikrofon", "Beyaz Tahta", "Yüksek Hızlı WiFi",
            "Sekreterya", "Çeviri Hizmeti", "Catering",
            "Düğün Organizasyonu", "Açık Hava Etkinlik Alanı",
        ],
    },
    {
        key: "transport",
        label: "Ulaşım",
        icon: "Car",
        color: "emerald",
        items: [
            "Havalimanı Servisi (Ücretli)", "Havalimanı Servisi (Ücretsiz)",
            "Şehir Merkezi Servisi", "Araç Kiralama", "Taksi Servisi",
            "Şarj İstasyonu (Elektrikli Araç)", "Bisiklet Kiralama",
            "Scooter Kiralama", "Motosiklet Kiralama",
        ],
    },
    {
        key: "other",
        label: "Diğer",
        icon: "Plus",
        color: "stone",
        items: [
            "Evcil Hayvan Kabul Edilir", "Evcil Hayvan Kabul Edilmez",
            "Sigara İçilmeyen Tesis", "Bahçe", "Teras",
            "Çatı Bahçesi", "Kütüphane", "Mescit",
            "Helikopter Pisti", "Yat Limanı", "Açık Hava Sineması",
            "Açık Hava Şömine",
        ],
    },
];

/** Returns a flat list of all selected items from the categorized dict. */
export function flattenAmenities(categorized) {
    if (!categorized || typeof categorized !== "object") return [];
    const seen = new Set();
    const out = [];
    Object.values(categorized).forEach((arr) => {
        (arr || []).forEach((item) => {
            if (!seen.has(item)) { seen.add(item); out.push(item); }
        });
    });
    return out;
}

/** Build categorized dict from legacy flat list by best-effort matching. */
export function bucketLegacyAmenities(flatList) {
    const result = {};
    AMENITY_CATEGORIES.forEach((c) => { result[c.key] = []; });
    if (!Array.isArray(flatList)) return result;
    flatList.forEach((item) => {
        let placed = false;
        for (const cat of AMENITY_CATEGORIES) {
            if (cat.items.includes(item)) {
                result[cat.key].push(item);
                placed = true;
                break;
            }
        }
        if (!placed) {
            // Drop into "other" so they aren't lost
            result.other = result.other || [];
            if (!result.other.includes(item)) result.other.push(item);
        }
    });
    return result;
}
